import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Bug, Send, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { getRecentErrors, getViewportInfo } from "@/lib/debugCapture";
import {
  captureScreenshot,
  getNetworkLog,
  getReplayEvents,
} from "@/lib/pageCapture";

export type AutoBugTrigger = {
  /** "runtime" for window/promise/console errors, "render" for React boundary crashes. */
  source: "runtime" | "render";
  message: string;
  stack?: string;
};

interface Props {
  open: boolean;
  trigger: AutoBugTrigger | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Auto-opens after an uncaught error/render crash. Captures a screenshot,
 * recent network log, rrweb replay, console breadcrumbs, route, viewport,
 * user agent — then asks the user for a one-line "what were you doing"
 * note and submits to the `bug-submit` edge function.
 */
export default function AutoBugReportDialog({ open, trigger, onOpenChange }: Props) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  // Snapshot context once when the dialog opens — capturing later would
  // miss whatever was on screen at the moment of the crash.
  const snapshotRef = useRef<{
    screenshot: Blob | null;
    replay: unknown[];
    network: ReturnType<typeof getNetworkLog>;
    breadcrumbs: ReturnType<typeof getRecentErrors>;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      snapshotRef.current = null;
      setNote("");
      return;
    }
    let cancelled = false;
    (async () => {
      const network = getNetworkLog();
      const breadcrumbs = getRecentErrors();
      const replay = getReplayEvents();
      const screenshot = await captureScreenshot();
      if (cancelled) return;
      snapshotRef.current = { screenshot, replay, network, breadcrumbs };
    })();
    return () => { cancelled = true; };
  }, [open]);

  const submit = async () => {
    if (loading || !trigger) return;
    setLoading(true);
    try {
      const snap = snapshotRef.current;
      const vp = getViewportInfo();

      // Upload screenshot + replay to storage if user is signed in.
      const uploads: { screenshot_path?: string; replay_path?: string } = {};
      if (user && snap) {
        const stamp = Date.now();
        if (snap.screenshot) {
          const path = `${user.id}/${stamp}-screenshot.jpg`;
          const { error } = await supabase.storage
            .from("bug-attachments")
            .upload(path, snap.screenshot, { contentType: "image/jpeg", upsert: false });
          if (!error) uploads.screenshot_path = path;
        }
        if (snap.replay.length > 0) {
          const path = `${user.id}/${stamp}-replay.json`;
          const blob = new Blob([JSON.stringify(snap.replay)], { type: "application/json" });
          const { error } = await supabase.storage
            .from("bug-attachments")
            .upload(path, blob, { contentType: "application/json", upsert: false });
          if (!error) uploads.replay_path = path;
        }
      }

      const { error } = await supabase.functions.invoke("bug-submit", {
        body: {
          source: trigger.source === "render" ? "runtime" : "runtime",
          title: trigger.message.slice(0, 200),
          description: note.slice(0, 2000),
          error_message: trigger.message.slice(0, 2000),
          error_stack: (trigger.stack || "").slice(0, 8000),
          route: pathname,
          url: typeof window !== "undefined" ? window.location.href : null,
          user_agent: vp.ua,
          attachments: {
            ...uploads,
            network_log: snap?.network ?? [],
            breadcrumbs: snap?.breadcrumbs ?? [],
            viewport: { width: vp.width, height: vp.height, dpr: vp.dpr },
          },
        },
      });
      if (error) throw error;
      toast.success("Bug reported — thanks! The team will review it.");
      onOpenChange(false);
    } catch (err) {
      console.error("auto bug-submit failed", err);
      toast.error("Couldn't send the report. You can try the manual bug button.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-rrweb-mask>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-mono">
            <Bug className="w-4 h-4 text-destructive" />
            Something went wrong
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">
            We caught an error on this page. Send a quick report so we can fix it.
          </DialogDescription>
        </DialogHeader>

        {trigger && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5">
            <p className="text-[10px] font-mono uppercase tracking-wider text-destructive/80 mb-0.5">
              Error
            </p>
            <p className="font-mono text-xs text-foreground break-words line-clamp-3">
              {trigger.message}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            What were you doing? <span className="text-muted-foreground/60">(optional)</span>
          </label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            disabled={loading}
            placeholder="e.g. clicked Generate, page froze"
            className="font-mono text-xs min-h-[80px]"
          />
        </div>

        <p className="text-[10px] font-mono text-muted-foreground">
          Auto-attached: screenshot, last 30s of activity, recent network calls, console log, route.
        </p>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-foreground font-mono text-xs hover:border-primary/40"
          >
            <X className="w-3.5 h-3.5" /> Dismiss
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground font-mono text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
            ) : (
              <><Send className="w-3.5 h-3.5" /> Send report</>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}